import React, { useState } from "react";
import { MapPin, Calendar, DollarSign, Clock, Star, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { CATEGORY_LABEL } from "@/data/careerTemplates";

function MetaItem({ icon: Icon, children, testid }) {
  if (!children) return null;
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-gray-600 uppercase tracking-widest" data-testid={testid}>
      <Icon size={12} className="text-br-bronze" /> {children}
    </div>
  );
}

/**
 * JobListingCard
 *
 * Public job-listing card for the /careers page. Shows the listing meta
 * up-front and lets the homeowner expand to read the full overview /
 * responsibilities / good fit / preferred experience / do not promise
 * sections. Primary CTA bubbles up via `onApply(listing)` so the parent
 * page can scroll to the application form + prefill the listing context.
 */
export default function JobListingCard({ listing, onApply }) {
  const [expanded, setExpanded] = useState(false);

  const blocks = [
    { label: "Position Overview", body: listing.overview },
    { label: "Responsibilities", body: listing.responsibilities },
    { label: "Good Fit For", body: listing.good_fit_for },
    { label: "Preferred Experience", body: listing.preferred_experience },
    { label: "Compensation Notes", body: listing.compensation_notes },
    { label: "Expectations / Important", body: listing.do_not_promise },
    { label: "Growth Path", body: listing.growth_path },
  ].filter((b) => b.body && b.body.trim());

  return (
    <div
      className={`relative bg-white border rounded-lg overflow-hidden ${listing.is_featured ? "border-br-bronze shadow-xl" : "border-gray-200"}`}
      data-testid={`job-card-${listing.id}`}
    >
      {listing.is_featured && (
        <div className="absolute top-3 right-3 bg-br-bronze text-white text-[9px] tracking-[0.25em] uppercase font-bold px-2 py-1 flex items-center gap-1 rounded">
          <Star size={9} style={{ fill: "#fff" }} /> Featured
        </div>
      )}
      <div className="p-6">
        <div className="br-eyebrow text-br-bronze">{CATEGORY_LABEL[listing.category] || listing.category}</div>
        <h3 className="font-display text-xl sm:text-2xl tracking-widest uppercase mt-1 text-br-black">{listing.title}</h3>

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          <MetaItem icon={MapPin} testid={`job-meta-location-${listing.id}`}>{listing.location}</MetaItem>
          <MetaItem icon={DollarSign} testid={`job-meta-comp-${listing.id}`}>{listing.compensation_type}</MetaItem>
          <MetaItem icon={Clock} testid={`job-meta-schedule-${listing.id}`}>{listing.schedule || listing.employment_type}</MetaItem>
          <MetaItem icon={Calendar} testid={`job-meta-start-${listing.id}`}>{listing.start_date}</MetaItem>
        </div>

        {listing.compensation_details && (
          <p className="text-xs text-gray-600 mt-3 leading-relaxed">
            <span className="font-semibold text-gray-800">Compensation:</span> {listing.compensation_details}
          </p>
        )}

        {blocks.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-br-bronze hover:text-br-bronze-2 font-semibold"
            data-testid={`job-expand-${listing.id}`}
          >
            {expanded ? <>Hide details <ChevronUp size={12} /></> : <>View details <ChevronDown size={12} /></>}
          </button>
        )}

        {expanded && (
          <div className="mt-4 space-y-4" data-testid={`job-details-${listing.id}`}>
            {blocks.map((b) => (
              <div key={b.label}>
                <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-1.5">{b.label}</div>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{b.body}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onApply(listing)}
            className="br-btn-primary"
            data-testid={`job-apply-${listing.id}`}
          >
            Apply for This Role <ArrowRight size={14} />
          </button>
          {listing.is_active_hiring ? (
            <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-600 font-bold">Actively Hiring</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">Open Pipeline</span>
          )}
        </div>
      </div>
    </div>
  );
}
